import * as React from "react";;
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useUserByOwnerId } from "../../../lib/apis/hooks/user.hook";
import type { matchStoreForm } from "../../../lib/apis/dtos/request/store.request";
import { matchStore } from "../../../lib/apis/v1/store.service";
import { getQueryClient } from "../../../config/queryClient.config";
import ConfirmModal from "../confirm.modal";
import type { ModalComponentProps } from "../../../types/modal.types";
import FailSnackbar from "../../ui/snackbar/fail.component";
import SuccessSnackbar from "../../ui/snackbar/success.component";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
  style: {
    zIndex: 3000
  }
};

interface AssignUserProps extends ModalComponentProps {
  storeId?: string;
  ownerId?: string;
}

export default function AssignUserModal(props: AssignUserProps) {
  const { id, storeId, onClose, ownerId } = props;

  const [user, setUser] = React.useState<string[]>([]);
  const [select, setSelect] = React.useState<number[]>([]);

  const { data: partnerData, isSuccess } = useQuery({
    ...useUserByOwnerId({ ownerId: ownerId || "", page: 0, size: 1000 }),
    enabled: !!ownerId,
  })
  const { result: partnerResult } = partnerData || {};
  const partnerContent = partnerResult?.content || [];

  const matchMutation = useMutation({
    mutationFn: (form: matchStoreForm) => matchStore(form),
    onSuccess: (res) => {
      const { error, message } = res;

      if (error) {
        return FailSnackbar(`담당자 배정 실패(${message})`)
      } else {
        getQueryClient().invalidateQueries({ queryKey: ["store-detail"] });
        SuccessSnackbar(`담당자 ${user.length}명 배정 완료`)
        onClose?.();
      }
    },
  });

  return (
    <ConfirmModal
      title="담당자 배정"
      onClose={onClose}
      rightButton={{
        title: "저장",
        onClick: () => {
          matchMutation.mutate({ id: storeId || "", users: select });
        },
      }}
      id={id}
    >
      <div className="flex flex-col gap-4 mt-4">
        <p className="py-3 text-[16px] text-black/60">
          지점을 담당할 담당자를 배정해주세요. 복수 선택 가능합니다.
        </p>
        <FormControl>
          <InputLabel id="demo-multiple-chip-label">담당자 선택*</InputLabel>
          <Select
            labelId="demo-multiple-chip-label"
            id="demo-multiple-chip"
            className="w-[552px]"
            multiple
            value={select}
            onChange={(e) => {
              const { target: { value } } = e;
              setSelect(typeof value === 'string' ? value.split(',').map(Number) : value);

              const selectedIds = typeof value === 'string' ? value.split(',').map(Number) : value;
              const selectedNames = selectedIds.map(id => {
                const found = partnerContent.find(u => u.userId === id);
                return found ? `${found.username}(${found.loginId})` : "";
              });
              setUser(selectedNames);
            }}
            input={<OutlinedInput id="select-multiple-chip" label="담당자 선택*" />}
            renderValue={(selectedIds) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selectedIds.map((id) => {
                  const userObj = partnerContent.find(u => u.userId === id);
                  return (
                    <Chip
                      key={id}
                      onMouseDown={(e) => e.stopPropagation()}
                      label={userObj ? `${userObj.username}(${userObj.loginId})` : id}
                      onDelete={() => {
                        setSelect((prev) => prev.filter((v) => v !== id));
                        setUser((prev) => prev.filter((_, i) => select[i] !== id));
                      }}
                    />
                  );
                })}
              </Box>
            )}
            MenuProps={MenuProps}
          >
            {isSuccess &&
              partnerContent.map(({ username, userId, loginId }) => (
                <MenuItem
                  key={userId}
                  value={userId}
                >
                  {username}({loginId})
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </div>
    </ConfirmModal>
  );
};
